import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Color, ECharts, EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import {
  HeatmapRequestParams,
  extraFiltersOnly,
} from 'src/app/common/dtos/reports.dto';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import {
  HeatmapData,
  HeatmapYAxisType,
} from 'src/app/private/shared/dtos/heatmap.dto';
import { ReportPageFiltersService } from '../../../services/report/report-page-filters.service';
import { FetchLastUpdatedDate } from '../../../state/reporting/reporting.actions';
import { getBlankChartOptions } from '../report-controller/chart-options-helper';

const heatmapFromResponse = (value: {
  xAxisLabels: string[];
  yAxisLabels: string[];
  yAxisIds?: number[];
  yAxisType?: HeatmapYAxisType;
  datasets: { dataRaw: number[][] }[];
}): HeatmapData => ({
  xLabels: value.xAxisLabels || [],
  yLabels: value.yAxisLabels || [],
  yAxisIds: value.yAxisIds || [],
  yAxisType: value.yAxisType || 'school',
  chartData: value.datasets?.[0]?.dataRaw || [],
});

@Component({
  selector: 'app-heatmap-controller',
  templateUrl: './heatmap-controller.component.html',
  styleUrls: ['./heatmap-controller.component.scss'],
  standalone: false,
})
export class HeatmapControllerComponent implements OnDestroy {
  heatmapOptions: EChartsOption;

  user: User;

  rootData: HeatmapData;

  nestedSchoolData: HeatmapData;

  learnerData: HeatmapData;

  eChartsInstance: ECharts;

  params: HeatmapRequestParams;

  innerWidth: number;

  isMobile: boolean;

  isLoading = true;

  heatmapLevel: HeatmapYAxisType = 'school';

  rootIsDistrictGrain = false;

  selectedDistrictId = 0;

  selectedSchoolId = 0;

  districtLabel = '';

  schoolLabel = '';

  perPage = 15;

  currentPage = 1;

  listMeta: PaginationDTO;

  _sub: Subscription;

  drillDownType = 'competency';

  @Output() readonly changeDrilldownType: EventEmitter<string> =
    new EventEmitter();

  @Output() readonly selectSchool: EventEmitter<number> = new EventEmitter();

  @Output() readonly selectDistrict: EventEmitter<number> = new EventEmitter();

  spinnerOptions = {
    text: 'Loading...',
    textColor: '#27004b',
    maskColor: 'rgba(255, 255, 255)',
    zlevel: 10,
    fontSize: 24,
    fontFamily: 'greycliff-cf',
    showSpinner: false,
  };

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
    let firstMobile;

    if (this.innerWidth <= 1000) {
      firstMobile = true;
    } else {
      firstMobile = false;
    }

    if (firstMobile !== this.isMobile) {
      const current = this.getCurrentChartData();
      if (current) {
        this.handleResponse(current);
      }
    }
  }

  constructor(
    private apiService: APICoreService,
    private store: Store,
    private reportPageFiltersService: ReportPageFiltersService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this._sub = this.reportPageFiltersService.filters.subscribe((filters) => {
      if (filters.schoolYearFilter && filters.reportName === 'badge_progress') {
        const filterParams = translateKpiReportFilterFromFilterDTO(filters);

        if (
          !this.params?.filters ||
          JSON.stringify(this.params.filters) !== JSON.stringify(filterParams)
        ) {
          this.resetDrillState();
          this.changeDrilldownType.emit('competency');

          this.params = {
            report: 'microCredentialProgressBySchools',
            filters: filterParams,
            report_filterset_id: filters.reportFiltersetId,
          };
          this.loadCurrentLevel();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  onChartInit(e: ECharts) {
    this.eChartsInstance = e;
    this.loadCurrentLevel();
  }

  paginationChange(page: number) {
    this.currentPage = page || this.listMeta?.currentPage || 1;
    this.loadCurrentLevel();
  }

  private resetDrillState() {
    this.heatmapLevel = 'school';
    this.rootIsDistrictGrain = false;
    this.selectedDistrictId = 0;
    this.selectedSchoolId = 0;
    this.districtLabel = '';
    this.schoolLabel = '';
    this.currentPage = 1;
    this.selectDistrict.emit(0);
    this.selectSchool.emit(0);
  }

  private getCurrentChartData(): HeatmapData | undefined {
    if (this.heatmapLevel === 'user') {
      return this.learnerData;
    }
    if (this.heatmapLevel === 'school' && this.rootIsDistrictGrain) {
      return this.nestedSchoolData;
    }
    return this.rootData;
  }

  private loadCurrentLevel(): void {
    if (!this.params) {
      return;
    }
    if (this.heatmapLevel === 'user' && this.selectedSchoolId) {
      this.getLearnerData(this.selectedSchoolId);
      return;
    }
    if (this.heatmapLevel === 'school' && this.selectedDistrictId) {
      this.getSchoolDataForDistrict(this.selectedDistrictId);
      return;
    }
    this.getRootData();
  }

  getRootData(): void {
    this.store.dispatch(FetchLastUpdatedDate);
    this.isLoading = true;
    const heatmapParams = {
      ...this.params,
      report: 'microCredentialProgressBySchools',
      page: this.currentPage,
      per_page: this.perPage,
      filters: extraFiltersOnly(
        this.params?.filters,
        this.params?.report_filterset_id
      ),
    };
    this.apiService
      .getRequest('reporting/load-report', heatmapParams)
      .subscribe({
        next: (value) => {
          this.rootData = heatmapFromResponse(value);
          this.rootIsDistrictGrain = this.rootData.yAxisType === 'district';
          this.heatmapLevel = this.rootData.yAxisType;
          this.listMeta = value._meta;
          this.innerWidth = window.innerWidth;
          this.handleResponse(this.rootData);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  getSchoolDataForDistrict(districtId: number): void {
    this.changeDrilldownType.emit('competency');
    this.selectDistrict.emit(districtId);
    this.isLoading = true;

    const districtFilter = {
      codename: 'district',
      title: 'District',
      value: [districtId.toString()],
      displayName: 'District',
    };
    const filtersetId = this.params.report_filterset_id;
    const extras =
      extraFiltersOnly(
        [...(this.params.filters || []), districtFilter],
        filtersetId,
        this.params.filters
      ) ?? [];
    const schoolParams = {
      report: 'microCredentialProgressBySchools',
      report_filterset_id: filtersetId,
      page: this.currentPage,
      per_page: this.perPage,
      filters: extras,
    };
    this.apiService
      .getRequest('reporting/load-report', schoolParams)
      .subscribe({
        next: (value) => {
          this.nestedSchoolData = heatmapFromResponse(value);
          this.heatmapLevel = 'school';
          this.listMeta = value._meta;
          this.innerWidth = window.innerWidth;
          this.handleResponse(this.nestedSchoolData);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  getLearnerData(schoolId: number): void {
    this.changeDrilldownType.emit('learner');
    this.isLoading = true;

    const schoolFilter = {
      codename: 'school',
      title: 'School',
      value: [schoolId.toString()],
      displayName: 'School',
    };

    this.selectSchool.emit(schoolId);

    const filtersetId = this.params.report_filterset_id;
    const extras =
      extraFiltersOnly(
        [...(this.params.filters || []), schoolFilter],
        filtersetId,
        this.params.filters
      ) ?? [];
    const extraFilters = [...extras];
    if (this.selectedDistrictId) {
      extraFilters.push({
        codename: 'district',
        title: 'District',
        value: [this.selectedDistrictId.toString()],
        displayName: 'District',
      });
    }
    const schoolParams = {
      report: 'microCredentialProgressByLearners',
      report_filterset_id: filtersetId,
      page: this.currentPage,
      per_page: this.perPage,
      filters: extraFilters,
    };
    this.apiService
      .getRequest('reporting/load-report', schoolParams)
      .subscribe({
        next: (value) => {
          this.learnerData = heatmapFromResponse({
            ...value,
            yAxisType: 'user',
          });
          this.heatmapLevel = 'user';
          this.listMeta = value._meta;
          this.innerWidth = window.innerWidth;
          this.handleResponse(this.learnerData);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  handleResponse(responseData: HeatmapData) {
    if (!this.eChartsInstance) {
      return;
    }
    const clickable = responseData.yAxisType !== 'user';
    if (!responseData?.chartData?.length) {
      this.eChartsInstance.setOption(getBlankChartOptions({}), true);
    } else {
      this.isMobile = this.innerWidth <= 992;
      let xZoomEnd: number;

      let dataZoomY = false;
      let dataZoomX = false;
      let yAxisWidth: number;
      let leftGridWidth: string;

      const tooltipYLabels = responseData.yLabels;
      const tooltipXLabels = responseData.xLabels;

      if (this.isMobile) {
        xZoomEnd = 2;
        yAxisWidth = 100;
        leftGridWidth = '130px';
      } else {
        xZoomEnd = 7;
        yAxisWidth = 230;
        leftGridWidth = '250px';
      }

      const dataZoomArray: EChartsOption['dataZoom'] = [];

      if (responseData.xLabels.length > 8 && !dataZoomX) {
        dataZoomArray.push({
          type: 'slider',
          showDetail: false,
          start: 0,
          endValue: xZoomEnd,
          xAxisIndex: 0,
          zoomLock: true,
          brushSelect: false,
          height: '20',
          backgroundColor: '#dfd0f3',
          borderColor: '#dfd0f3',
          fillerColor: 'white',
          handleSize: 33,
          handleStyle: {
            borderColor: '#dfd0f3',
          },
          emphasis: {
            handleStyle: {
              borderColor: '#dfd0f3',
            },
            handleLabel: {
              show: false,
            },
          },
        });

        dataZoomX = true;
      } else {
        dataZoomArray.push({
          show: false,
          xAxisIndex: 0,
        });
      }

      if (responseData.yLabels.length > 8 && !dataZoomY) {
        dataZoomArray.push({
          type: 'slider',
          showDetail: false,
          startValue: responseData.yLabels.length - 10,
          endValue: responseData.yLabels.length - 1,
          yAxisIndex: 0,
          zoomLock: true,
          brushSelect: false,
          width: '20',
          backgroundColor: '#dfd0f3',
          borderColor: '#dfd0f3',
          fillerColor: 'white',
          handleSize: 33,
          handleStyle: {
            borderColor: '#dfd0f3',
          },
          emphasis: {
            handleStyle: {
              borderColor: '#dfd0f3',
            },
            handleLabel: {
              show: false,
            },
          },
        });

        dataZoomY = true;
      } else {
        dataZoomArray.push({
          show: false,
          yAxisIndex: 0,
        });
      }

      let gridWidth;
      if (this.isMobile) {
        gridWidth = 'auto';
      } else if (responseData.xLabels.length > 5) {
        if (this.innerWidth > 1400) {
          gridWidth = '76%';
        } else if (this.innerWidth > 1200) {
          gridWidth = '70%';
        } else if (this.innerWidth > 900) {
          gridWidth = '65%';
        }
      } else {
        gridWidth = `${responseData.xLabels.length * 150}px`;
      }

      const titleText = this.getChartTitle();
      const heatmapOptions = {
        tooltip: {
          backgroundColor: '#f0cedf',
          borderColor: '#f0cedf',
          textStyle: { color: 'black', fontFamily: 'greycliff-cf' },
          position: 'top',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter(params: any): string {
            return `
            <strong>
              ${tooltipYLabels[params.data[1]]}: ${tooltipXLabels[
              params.data[0]
            ].replace(/\(id: .*\)/, '')}
            </strong>
            <br/>
            ${params.data[2]}/${params.data[3]} (${params.data[4].toFixed(2)}%)
            `;
          },
        },
        dataZoom: dataZoomArray,
        grid: {
          height:
            responseData.yLabels.length * 75 > 620
              ? 620
              : responseData.yLabels.length * 75,
          top: '75px',
          left: leftGridWidth,
          width: gridWidth,
        },
        title: {
          textStyle: {
            fontSize: 18,
            fontWeight: 'lighter',
          },
          text: titleText,
          show: !!titleText,
        },
        graphic: {
          type: 'group',
          top: 5,
          right: 0,
          tooltip: {
            show: false,
          },
          ignore: this.heatmapLevel === this.rootData?.yAxisType,
          onclick: this.onBackClick.bind(this),
          children: [
            {
              tooltip: {
                backgroundColor: '',
                borderColor: '',
                show: false,
              },
              type: 'image',
              style: {
                image: 'assets/report/back-arrow.svg',
                fill: 'red',
                width: 20,
                height: 20,
              },
            },
            {
              tooltip: {
                backgroundColor: '',
                borderColor: '',
                show: false,
              },
              type: 'text',
              top: 3,
              right: 8,
              style: {
                text: 'Back',
                fontSize: 18,
                fontWeight: 'lighter',
              },
              textConfig: {
                position: 'right',
                offset: [50, 50],
              },
            },
          ],
        },
        xAxis: {
          type: 'category',
          data: responseData.xLabels,
          splitArea: {
            show: true,
            areaStyle: {
              color: [
                'rgba(255,255,255,1)' as Color,
                'rgba(255,255,255,1)' as Color,
              ],
            },
          },
          position: 'top',
          axisLabel: {
            fontFamily: 'greycliff-cf',
            interval: 0,
            width: 80,
            overflow: 'break',
            formatter(value: string) {
              const maxHeight = 35;
              const label =
                value.length > maxHeight
                  ? `${value.slice(0, maxHeight)}...`
                  : value;
              return label;
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: 'category',
          data: responseData.yLabels,
          splitArea: {
            show: true,
          },
          axisLabel: {
            fontFamily: 'greycliff-cf',
            interval: 0,
            width: yAxisWidth,
            fontSize: 15,
            color: clickable ? '#1272a6' : 'gray',
            overflow: 'break',
            rich: {
              title: {
                width: 100,
                emphasis: {
                  itemStyle: {
                    color: 'green',
                  },
                },
              },
            },
          },
          triggerEvent: clickable,
          axisTick: {
            show: false,
          },
        },
        visualMap: {
          show: false,
          type: 'piecewise',
          min: 0,
          max: 100,
          calculable: true,
          pieces: [
            { min: 0, max: 0.009, color: 'rgb(250, 249, 246, .75)' },
            { min: 0.01, max: 25, color: '#f0eee9' },
            { min: 25.01, max: 50, color: '#C6DEEC' },
            { min: 50.01, max: 74.99, color: '#6FADCF' },
            { min: 75, max: 99.99, color: '#267DAD' },
            { min: 100, max: 100, color: '#21B0FF' },
          ],
        },
        series: [
          {
            name: '',
            type: 'heatmap',
            data: responseData.chartData,
            label: {
              show: false,
            },
            emphasis: {
              disabled: true,
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: 'white',
              borderRadius: 5,
            },
          },
        ],
      };
      this.eChartsInstance.setOption(heatmapOptions, true);
    }
  }

  private getChartTitle(): string {
    if (this.heatmapLevel === 'user') {
      return this.schoolLabel;
    }
    if (this.heatmapLevel === 'school' && this.rootIsDistrictGrain) {
      return this.districtLabel;
    }
    return '';
  }

  onBackClick() {
    this.currentPage = 1;
    if (this.heatmapLevel === 'user') {
      this.selectSchool.emit(0);
      this.selectedSchoolId = 0;
      this.schoolLabel = '';
      this.changeDrilldownType.emit('competency');
      if (this.rootIsDistrictGrain && this.selectedDistrictId) {
        this.heatmapLevel = 'school';
        this.getSchoolDataForDistrict(this.selectedDistrictId);
        return;
      }
      this.heatmapLevel = this.rootData?.yAxisType || 'school';
      this.getRootData();
      return;
    }
    if (this.heatmapLevel === 'school' && this.rootIsDistrictGrain) {
      this.selectDistrict.emit(0);
      this.selectedDistrictId = 0;
      this.districtLabel = '';
      this.heatmapLevel = 'district';
      this.getRootData();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClickEvent(event: any) {
    if (event.componentType !== 'yAxis') {
      return;
    }
    const current = this.getCurrentChartData();
    if (!current?.yAxisIds?.length) {
      return;
    }
    const entityId = Number(current.yAxisIds[event.dataIndex]);
    if (!entityId) {
      return;
    }
    this.currentPage = 1;
    this.isLoading = true;
    if (current.yAxisType === 'district') {
      this.districtLabel = event.value;
      this.selectedDistrictId = entityId;
      this.getSchoolDataForDistrict(entityId);
      return;
    }
    if (current.yAxisType === 'school') {
      this.schoolLabel = event.value;
      this.selectedSchoolId = entityId;
      this.getLearnerData(entityId);
    }
  }
}
